// Semantic table text-color tokens and helpers for SABER read screens.
// Apply roles (positive, negative, warning, …) — not raw Tailwind per table.

/** Light/dark Tailwind classes for each semantic cell role. */
export const CELL_COLOR = {
  positive: 'text-emerald-600 dark:text-emerald-400',
  negative: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  /** Tariff column wrapper; schedule labels use StatusBadge palette="tariff". */
  tariff: 'text-blue-600 dark:text-blue-400',
  primary: 'text-slate-900 dark:text-slate-100',
  secondary: 'text-slate-500 dark:text-slate-400',
  muted: 'text-slate-700 dark:text-slate-200',
  null: 'text-slate-400 dark:text-slate-500'
} as const

export type CellColorRole = keyof typeof CELL_COLOR

/** Revenue / dollar columns: emerald when ≥ 0, red when negative. */
export const moneyColorClass = (value: number | null | undefined): string | undefined => {
  if (value == null || Number.isNaN(value)) return undefined
  if (value < -0.005) return CELL_COLOR.negative
  if (value >= 0) return CELL_COLOR.positive
  return undefined
}

/** Signed kWh delta: negative → red (loss / shortfall). Zero/neutral → undefined. */
export const signedKwhDeltaColorClass = (value: number | null | undefined): string | undefined => {
  if (value == null || Number.isNaN(value) || value === 0) return undefined
  return value < 0 ? CELL_COLOR.negative : CELL_COLOR.positive
}

/** Loss columns (unbilled kWh/$, over-alloc): red when above threshold. */
export const lossWhenPositive = (value: number, threshold = 0): string | undefined =>
  value > threshold ? CELL_COLOR.negative : undefined

/** Over-allocation kWh: amber when above threshold. */
export const warningWhenPositive = (value: number, threshold = 0): string | undefined =>
  value > threshold ? CELL_COLOR.warning : undefined

/** §9 completeness % text: ≥99 emerald · ≥90 amber · <90 red. */
export const completenessPctColorClass = (pct: number): string =>
  pct >= 99 ? CELL_COLOR.positive : pct >= 90 ? CELL_COLOR.warning : CELL_COLOR.negative

/** Bar fill colors paired with completenessPctColorClass. */
export const completenessBarClass = (pct: number): string =>
  pct >= 99
    ? 'bg-emerald-500 dark:bg-emerald-400'
    : pct >= 90
      ? 'bg-amber-500 dark:bg-amber-400'
      : 'bg-red-500 dark:bg-red-400'

/** Pro-Forma §10 performance % text: ≥97 emerald · ≥90 amber · <90 red. */
export const performancePctColorClass = (pct: number | null): string | undefined => {
  if (pct == null) return undefined
  if (pct >= 97) return CELL_COLOR.positive
  if (pct >= 90) return CELL_COLOR.warning
  return CELL_COLOR.negative
}

/** Pro-Forma signed Δ kWh vs model: above model emerald, below red. */
export const kwhModelDeltaColorClass = (delta: number | null): string | undefined => {
  if (delta == null || delta === 0) return undefined
  return delta > 0 ? CELL_COLOR.positive : CELL_COLOR.negative
}

/** Dashboard % of forecast: ≥95 emerald · ≥80 indigo · ≥50 amber · <50 red. */
export const forecastPctColorClass = (pct: number): string => {
  if (pct >= 95) return CELL_COLOR.positive
  if (pct >= 80) return 'text-indigo-600 dark:text-indigo-400'
  if (pct >= 50) return CELL_COLOR.warning
  return CELL_COLOR.negative
}

/** Bar fill colors paired with forecastPctColorClass. */
export const forecastBarClass = (pct: number): string => {
  if (pct >= 95) return 'bg-emerald-500 dark:bg-emerald-400'
  if (pct >= 80) return 'bg-indigo-500 dark:bg-indigo-400'
  if (pct >= 50) return 'bg-amber-500 dark:bg-amber-400'
  return 'bg-red-500 dark:bg-red-400'
}

/**
 * Total-variance $ coloring: emerald when SABER expected less (negative), red when
 * billed more (positive), neutral slate at zero (including values that round to $0.00).
 */
export const varianceColorClass = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value) || Math.abs(value) < 0.005) {
    return CELL_COLOR.muted
  }
  return value < 0 ? CELL_COLOR.positive : CELL_COLOR.negative
}

/** Variance effect $ in drawer: same semantics as varianceColorClass. */
export const varianceEffectColorClass = (value: number): string => {
  if (Number.isNaN(value) || Math.abs(value) < 0.005) {
    return CELL_COLOR.muted
  }
  return value < 0 ? CELL_COLOR.positive : CELL_COLOR.negative
}
